import { useMemo } from "react";

import { useErrorMessageGenerator } from "../../../common/hooks/use-error-message-generator";
import { useFilterContainerStates } from "../../../service/containers/useFilterContainerStates";
import type { ContainerWithState } from "../../../service/containers/useGetContainerStates";
import { EMPTY, FILLED, NON_EXISTENT } from "../../../service/containers/useGetContainerStates";

/**
 * Calculates a validation error message based on the provided container states and the current mode.
 * Prioritizes "non-existent" errors over "filled" (for fill mode) or "empty" (for empty mode) errors.
 *
 * @param mode - "fill" or "empty" to determine which state constitutes an error.
 * @param containerStates - The list of container states to validate.
 * @returns A validation error message string, or an empty string if no validation errors are found.
 */
export function useSelectionError(mode: "fill" | "empty", containerStates: ContainerWithState[]) {
  const determineContainerStates = useFilterContainerStates();
  const errorMessageGenerator = useErrorMessageGenerator();

  return useMemo(() => {
    if (mode === "fill") {
      return (
        errorMessageGenerator(
          determineContainerStates(containerStates, [NON_EXISTENT]).map(
            (containerState) => containerState.containerName,
          ),
          "Behälter {names} existiert nicht. Bitte vorher erstellen!",
          "Behälter {names} existieren nicht. Bitte vorher erstellen!",
          "{names}",
        ) ||
        errorMessageGenerator(
          determineContainerStates(containerStates, [FILLED]).map((containerState) => containerState.containerName),
          "Behälter {names} ist bereits befüllt. Bitte vorher entleeren!",
          "Behälter {names} sind bereits befüllt. Bitte vorher entleeren!",
          "{names}",
        )
      );
    } else if (mode === "empty") {
      return (
        errorMessageGenerator(
          determineContainerStates(containerStates, [NON_EXISTENT]).map(
            (containerState) => containerState.containerName,
          ),
          "Behälter {names} existiert nicht!",
          "Behälter {names} existieren nicht!",
          "{names}",
        ) ||
        errorMessageGenerator(
          determineContainerStates(containerStates, [EMPTY]).map((containerState) => containerState.containerName),
          "Behälter {names} ist nicht befüllt!",
          "Behälter {names} sind nicht befüllt!",
          "{names}",
        )
      );
    }
    return "";
  }, [containerStates, determineContainerStates, errorMessageGenerator, mode]);
}
